import { z } from 'zod';
import { nonEmptyPatch } from '../_patch.js';
import { CommercialProposalCalculationOutputSchema } from './calculation.js';
import { CommercialProposalContentSchema } from './content.js';
import { CommercialProposalDetailSchema, CommercialProposalListItemSchema } from './wire.js';
import { CommercialProposalPublicViewKindSchema } from './status.js';

/**
 * @aiPurpose input for `POST /api/commercial-proposals` — create a DRAFT
 *   owned by the actor (#1195). The whole editable content object is
 *   supplied verbatim; the backend computes and persists the totals.
 * @aiInvariant `.strict()`; `content` is validated by
 *   `CommercialProposalContentSchema`. The backend assigns
 *   `ownerId = actor.sub` and `status = DRAFT`. The optional top-level
 *   `leadId` (#2311) binds the КП to a CRM Lead at create; the server
 *   applies an owner/admin lead-visibility guard and the link is fixed at
 *   create (PATCH does NOT accept it). Absent → unlinked.
 */
export const CreateCommercialProposalInputSchema = z
  .object({
    content: CommercialProposalContentSchema,
    /// Optional CRM Lead to bind this КП to at create (#2311). Server-side
    /// owner/admin lead-visibility guard applies; the link is fixed at create
    /// (PATCH does not accept it). Absent → unlinked.
    leadId: z.string().uuid().optional(),
  })
  .strict();
export type CreateCommercialProposalInput = z.infer<typeof CreateCommercialProposalInputSchema>;

/**
 * @aiPurpose input for `PATCH /api/commercial-proposals/:id` — replace the
 *   editable content of a DRAFT (#1195). Editing a PUBLISHED proposal is a
 *   409 domain conflict; the frozen snapshot is immutable.
 * @aiInvariant `nonEmptyPatch` rejects `{}`. The only patchable field is
 *   `content`, which is replaced wholesale (the form is a single document);
 *   the backend recomputes the totals from the new content.
 */
const CommercialProposalPatchInnerSchema = z
  .object({
    content: CommercialProposalContentSchema,
  })
  .strict();
export const PatchCommercialProposalInputSchema = nonEmptyPatch(
  CommercialProposalPatchInnerSchema.partial().strict(),
);
export type PatchCommercialProposalInput = z.infer<typeof PatchCommercialProposalInputSchema>;

/**
 * @aiPurpose owner/Admin reveal envelope for the active public link of a
 *   PUBLISHED proposal (#1195). Carries the recoverable bearer token plus a
 *   relative landing path so the "copy link" UX re-displays the SAME link
 *   repeatedly.
 * @aiSecurity `token` is a high-entropy bearer secret. It appears ONLY on
 *   the owner/Admin detail/publish envelope — never on a list row, the
 *   public DTO, or any unauthenticated surface. REDACT in logs.
 * @aiInvariant `path` carries the token in the URL FRAGMENT (`#t=`) so it is
 *   never sent to the server in a query string and never lands in access
 *   logs or a `Referer` header. The customer-facing SPA reads the fragment
 *   and POSTs the token to the public resolve endpoint.
 */
export const CommercialProposalPublicLinkSchema = z
  .object({
    token: z.string().min(1),
    path: z.string().min(1),
  })
  .strict();
export type CommercialProposalPublicLink = z.infer<typeof CommercialProposalPublicLinkSchema>;

/**
 * @aiPurpose owner/Admin detail response for the authenticated CP read +
 *   create/patch/publish/revoke return (#1195). Wraps the frozen
 *   `CommercialProposalDetail` and adds the public-link reveal as a sibling
 *   envelope so the immutable `.strict()` Detail contract is never widened
 *   with token fields.
 * @aiInvariant `publicLink` is non-null exactly when `proposal.status ===
 *   PUBLISHED` (a DRAFT has no active token). The exact coupling is enforced
 *   server-side, not re-asserted here, to keep the wire contract additive.
 *   `calculation` ALWAYS carries the backend-authoritative totals of the
 *   CURRENT content: live-recomputed for a DRAFT (the frozen `snapshot` is
 *   null while editing) and the frozen snapshot totals once PUBLISHED — so
 *   the editor renders totals before publish without re-implementing the
 *   formula client-side.
 * @aiSecurity owner/Admin-only; `publicLink.token` is the recoverable
 *   bearer secret (see `CommercialProposalPublicLinkSchema`). `calculation`
 *   may include `paymentDivisor` because this is the owner's own data — it
 *   is NOT the public DTO, which omits divisors.
 */
export const CommercialProposalDetailResponseSchema = z
  .object({
    proposal: CommercialProposalDetailSchema,
    calculation: CommercialProposalCalculationOutputSchema,
    publicLink: CommercialProposalPublicLinkSchema.nullable(),
  })
  .strict();
export type CommercialProposalDetailResponse = z.infer<
  typeof CommercialProposalDetailResponseSchema
>;

/**
 * @aiPurpose response envelope for `GET /api/commercial-proposals` (#1195).
 * @aiInvariant `.strict()`; ordered most-recently-updated first by the
 *   backend. No cursor in the MVP — the workspace list is bounded.
 */
export const CommercialProposalListResponseSchema = z
  .object({
    items: z.array(CommercialProposalListItemSchema),
  })
  .strict();
export type CommercialProposalListResponse = z.infer<typeof CommercialProposalListResponseSchema>;

/**
 * @aiPurpose body of the UNAUTHENTICATED public resolve endpoint
 *   `POST /api/public/commercial-proposals/resolve` (#1195). The bearer
 *   token travels in the body (not the URL) so it never lands in access
 *   logs.
 * @aiSecurity an unknown / malformed / draft / revoked token MUST yield the
 *   IDENTICAL not-found behavior as a syntactically valid-but-unmatched
 *   token — no existence leak. The shape cap bounds the token length so a
 *   pathological payload cannot drive an expensive hash.
 */
export const ResolvePublicCommercialProposalInputSchema = z
  .object({
    token: z.string().min(1).max(512),
  })
  .strict();
export type ResolvePublicCommercialProposalInput = z.infer<
  typeof ResolvePublicCommercialProposalInputSchema
>;

/**
 * @aiPurpose one public-view analytics row for the owner/Admin analytics
 *   read `GET /api/commercial-proposals/:id/views` (#1195).
 * @aiPii `ipAddress` is the client IP that opened the public sheet and
 *   `userAgent` is a truncated fingerprint. Owner/Admin-only — never
 *   surfaced on the public DTO or to non-owners.
 */
export const CommercialProposalViewItemSchema = z
  .object({
    id: z.string().uuid(),
    kind: CommercialProposalPublicViewKindSchema,
    viewedAt: z.string().datetime(),
    ipAddress: z.string(),
    userAgent: z.string().nullable(),
  })
  .strict();
export type CommercialProposalViewItem = z.infer<typeof CommercialProposalViewItemSchema>;

/**
 * @aiPurpose response envelope for the owner/Admin analytics read (#1195).
 * @aiInvariant `.strict()`; ordered most-recent-first. `total` is the
 *   LIFETIME HTML_VIEW + PDF_DOWNLOAD count surviving revoke/republish.
 */
export const CommercialProposalViewsResponseSchema = z
  .object({
    items: z.array(CommercialProposalViewItemSchema),
    total: z.number().int().nonnegative(),
  })
  .strict();
export type CommercialProposalViewsResponse = z.infer<typeof CommercialProposalViewsResponseSchema>;
